import AppButton from '@nxsys/components/AppButton';
import { AppConfirmation } from '@nxsys/components/AppConfirmationModal';
import { formatThousands } from '@nxsys/components/NumericInput/format';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppPayFrequencySelect from '@nxsys/components/AppPayFrequencySelect';
import { PayFrequencyValue } from '@nxsys/components/AppPayFrequencySelect/type';
import AppSelect from '@nxsys/components/AppSelect';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import TaxWeekMonthSelect from '@nxsys/components/AppTaxWeekMonthSelect';
import { PaymentFrequencyEnum } from '@nxsys/constants/AppEnums';
import {
  useSaveImportTimesheetById,
  useValidateImportTimesheet,
} from '@nxsys/hooks/api/import';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { EmployerSelection } from '@nxsys/types/api/import';
import { EmployerOption } from '@nxsys/types/models/import';
import { showCurrencyByCountry } from '@nxsys/utils';
import { sumRateLines } from '@nxsys/utils/exact-money';
import { showMessage } from '@nxsys/utils/message';
import { getCurrentTaxMonth, getCurrentTaxWeek } from '@nxsys/utils/taxYear';
import { Modal, Select, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { IMPORT_COLUMNS_WIDTH } from '../constants';
import UploadedFileInformation from './UploadedFileInformation';

type ImportTimesheetResultUploadModalProps = {
  isOpen: boolean;
  toggle: () => void;
  resultData: IImportTimesheet | null;
  loading: boolean;
  refetchData?: () => void;
};

const importTimesheetTemplateOptions = [
  {
    label: 'Nxsys Default',
    value: 'NEXUS_DEFAULT',
  },
];

const AMBIGUOUS_ERROR_CODE = 'AGENCY_REF_AMBIGUOUS_EMPLOYER';

const ImportTimesheetResultUploadModal: React.FC<
  ImportTimesheetResultUploadModalProps
> = ({ isOpen, toggle, resultData, loading, refetchData }) => {
  const { messages, formatMessage } = useTypedIntl();
  const { payrollCountry, isBureau } = useSelector(
    (state: AppState) => state.appStore,
  );

  const {
    run: saveImportTimesheetById,
    loading: saveImportTimesheetByIdLoading,
  } = useSaveImportTimesheetById();

  const {
    run: validateImportTimesheet,
    loading: validateImportTimesheetLoading,
  } = useValidateImportTimesheet();

  const [isValidated, setIsValidated] = useState(false);
  const [selectedPayFrequency, setSelectedPayFrequency] =
    useState<PayFrequencyValue | null>(null);
  const [selectedTaxPeriod, setSelectedTaxPeriod] = useState<number | null>(
    null,
  );

  // Local per-row employer selections: rowIndex -> companyPaymentId
  const [employerSelectionMap, setEmployerSelectionMap] = useState<
    Record<number, string>
  >({});

  // Row whose employer picker popup is open (null = closed)
  const [employerPickerRowIndex, setEmployerPickerRowIndex] = useState<
    number | null
  >(null);

  // Bulk checkbox selection — only ambiguous rows are selectable
  const [selectedAmbiguousRowKeys, setSelectedAmbiguousRowKeys] = useState<
    number[]
  >([]);
  const [bulkEmployerValue, setBulkEmployerValue] = useState<
    string | undefined
  >(undefined);

  // resultData can be refreshed from re-validate response
  const [localResultData, setLocalResultData] =
    useState<IImportTimesheet | null>(null);

  const activeData = localResultData ?? resultData;

  const currentTaxWeek = getCurrentTaxWeek()?.week ?? null;
  const currentTaxMonth = getCurrentTaxMonth()?.taxMonth ?? null;

  const {
    data = [],
    fileName,
    fileSize,
    totalRecords = 0,
    headersImportFile,
    isCorrectAllRecords,
    id,
    dataChecked = [],
  } = activeData || {};

  type MergedRow = (typeof dataChecked)[number] & {
    needEmployerSelection?: boolean;
    employerOptions?: EmployerOption[];
    selectedCompanyPaymentId?: string;
    ambiguousAgencyRef?: string | null;
  };

  // Merge server dataChecked with local employer selections
  const mergedDataChecked = useMemo<MergedRow[]>(() => {
    return dataChecked.map((row, idx) => ({
      ...row,
      selectedCompanyPaymentId:
        employerSelectionMap[idx] ?? (row as MergedRow).selectedCompanyPaymentId,
    }));
  }, [dataChecked, employerSelectionMap]);

  const ambiguousRowIndices = useMemo<number[]>(() => {
    return mergedDataChecked
      .map((row, idx) => (row.needEmployerSelection ? idx : -1))
      .filter((idx) => idx !== -1);
  }, [mergedDataChecked]);

  const hasAmbiguousRows = ambiguousRowIndices.length > 0;

  const allAmbiguousResolved = useMemo(() => {
    return ambiguousRowIndices.every(
      (idx) => !!mergedDataChecked[idx]?.selectedCompanyPaymentId,
    );
  }, [ambiguousRowIndices, mergedDataChecked]);

  const unresolvedRowCount = useMemo(
    () =>
      ambiguousRowIndices.filter(
        (idx) => !mergedDataChecked[idx]?.selectedCompanyPaymentId,
      ).length,
    [ambiguousRowIndices, mergedDataChecked],
  );

  // Only offer employers that are a candidate for EVERY ticked row (intersection).
  // A union would let the user apply an employer that isn't valid for some rows,
  // which silently fails those rows on re-validate.
  const bulkEmployerOptions = useMemo(() => {
    const common = selectedAmbiguousRowKeys.reduce<EmployerOption[] | null>(
      (acc, idx) => {
        const opts = mergedDataChecked[idx]?.employerOptions ?? [];
        if (acc === null) return opts;
        return acc.filter((c) =>
          opts.some((o) => o.companyPaymentId === c.companyPaymentId),
        );
      },
      null,
    );
    return (common ?? []).map((o) => ({
      label: o.companyName,
      value: o.companyPaymentId,
    }));
  }, [selectedAmbiguousRowKeys, mergedDataChecked]);

  // Strictly per-row: the same reference can legitimately point at different workers
  // under different employers within one file (that is the whole reason the reference
  // is ambiguous), so resolving one row must never touch another.
  const applyEmployerToRows = (
    rowIndices: number[],
    companyPaymentId: string,
  ) => {
    setEmployerSelectionMap((prev) => {
      const updated = { ...prev };
      rowIndices.forEach((idx) => {
        updated[idx] = companyPaymentId;
      });
      return updated;
    });
  };

  const handleRowEmployerSelect = (rowIndex: number, value: string) => {
    applyEmployerToRows([rowIndex], value);
  };

  const handleBulkApply = () => {
    if (!bulkEmployerValue) return;
    applyEmployerToRows(selectedAmbiguousRowKeys, bulkEmployerValue);
    setSelectedAmbiguousRowKeys([]);
    setBulkEmployerValue(undefined);
  };

  const handleAmbiguousSelectionChange = (keys: number[]) => {
    setSelectedAmbiguousRowKeys(keys);
    // The valid employer set is the intersection over the ticked rows, so a pick made
    // for a previous selection may no longer be offered.
    setBulkEmployerValue(undefined);
  };

  // Pushes the employer picks to the server and returns the fresh validation slice, so
  // the caller can branch on the updated isCorrectAllRecords without waiting for a
  // state round-trip.
  const runReValidate = async () => {
    if (!id) return null;

    const employerSelections: EmployerSelection[] = ambiguousRowIndices
      .filter((idx) => !!mergedDataChecked[idx]?.selectedCompanyPaymentId)
      .map((idx) => ({
        rowIndex: idx,
        companyPaymentId: mergedDataChecked[idx].selectedCompanyPaymentId!,
      }));

    if (employerSelections.length === 0) return null;

    try {
      const res = await validateImportTimesheet({
        id,
        headerCompare: headersImportFile ?? [],
        isBureau,
        employerSelections,
      });
      if (!res?.success || !res?.data) return null;
      // The validate endpoint returns only the re-parsed validation slice — it has
      // no id/fileName/fileSize/totalRecords/headersImportFile. Merge over the
      // current record so those survive; replacing would blank the whole modal and
      // strip the id needed to re-validate again.
      setLocalResultData((prev) => ({
        ...((prev ?? resultData) as IImportTimesheet),
        ...res.data,
      }));
      // Reset local selection map — BE has persisted resolved refs
      setEmployerSelectionMap({});
      return res.data;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const columns: ColumnsType<any> = useMemo(() => {
    if (!isValidated) {
      return (
        headersImportFile?.map((columnHeader, index) => ({
          key: `${columnHeader}_${index}`,
          title: <div className="truncate">{columnHeader}</div>,
          dataIndex: index,
          width:
            IMPORT_COLUMNS_WIDTH?.[
              columnHeader as keyof typeof IMPORT_COLUMNS_WIDTH
            ] ?? 200,
        })) ?? []
      );
    } else {
      const validationColumns = [
        ...(headersImportFile ?? []),
        // Only worth a column when something actually needs resolving — its cell renders
        // nothing for unambiguous rows.
        ...(hasAmbiguousRows ? ['Employer'] : []),
        'Result',
        'Message',
      ];

      return (
        validationColumns?.map((columnHeader, index) => ({
          key: `${columnHeader}_${index}`,
          title: columnHeader,
          dataIndex: index,
          width:
            IMPORT_COLUMNS_WIDTH?.[
              columnHeader as keyof typeof IMPORT_COLUMNS_WIDTH
            ] ?? 200,
          render: (value: any, _: any, dataIndex: number) => {
            const record = mergedDataChecked[dataIndex] as MergedRow | undefined;
            const listErrors: any[] = record?.listRowsUncorrect ?? [];
            const isAmbiguous = listErrors.some(
              (e: any) => e?.code === AMBIGUOUS_ERROR_CODE,
            );
            const errorMsg = listErrors
              .filter((e: any) => e?.code !== AMBIGUOUS_ERROR_CODE)
              .map((item: any) => item?.errorMessage)
              .join('\n');
            const hasError = Boolean(errorMsg) || (isAmbiguous && !record?.selectedCompanyPaymentId);

            let valueRender: React.ReactNode = value;

            if (columnHeader === 'Employer') {
              if (record?.needEmployerSelection) {
                const selected = (record.employerOptions ?? []).find(
                  (o: EmployerOption) =>
                    o.companyPaymentId === record.selectedCompanyPaymentId,
                );
                return (
                  <button
                    type="button"
                    className={`w-full text-left underline underline-offset-2 ${
                      selected ? '!text-green-600' : '!text-red-500'
                    }`}
                    onClick={() => setEmployerPickerRowIndex(dataIndex)}
                  >
                    {selected ? selected.companyName : 'Select employer'}
                  </button>
                );
              }
              return null;
            }

            if (columnHeader === 'Message') {
              const ambiguousMsg = isAmbiguous
                ? 'Ambiguous employer — please select one above.'
                : '';
              const displayMsg = [ambiguousMsg, errorMsg]
                .filter(Boolean)
                .join('\n');
              valueRender = (
                <p
                  className={`whitespace-pre-wrap text-left ${
                    hasError ? '!text-red-500' : '!text-green-500'
                  }`}
                >
                  {displayMsg || undefined}
                </p>
              );
            }

            if (columnHeader === 'Result') {
              valueRender = (
                <p className={hasError ? '!text-red-500' : '!text-green-500'}>
                  {hasError
                    ? messages['common.failed']
                    : messages['common.success']}
                </p>
              );
            }

            return (
              <div
                className={`${hasError && isValidated ? '!text-red-500' : ''}`}
              >
                {valueRender}
              </div>
            );
          },
        })) || []
      );
    }
  }, [isValidated, headersImportFile, messages, mergedDataChecked]);

  const dataTable = data.length > 1 ? data?.slice(1) : [];

  const handleValidate = async () => {
    if (totalRecords === 0) {
      showMessage({
        content: messages['common.noDataToImport'],
        type: 'error',
      });

      return;
    }

    if (!selectedPayFrequency) {
      showMessage({
        type: 'error',
        content: messages['common.pleaseSelectPayFrequency'],
      });
      return;
    }

    if (!selectedTaxPeriod) {
      showMessage({
        type: 'error',
        content: messages['common.pleaseSelectTaxPeriod'],
      });
      return;
    }

    if (
      (dataChecked.length ? dataChecked : []).findIndex(
        (e) =>
          (e?.subcontractorRef || e?.companyRef) &&
          selectedPayFrequency !==
            (e?.subcontractorRef?.payFrequency ||
              e?.companyRef?.paymentFrequency),
      ) >= 0
    ) {
      showMessage({
        type: 'error',
        content: messages['import.timesheet.payFrequencyAndTaxPeriodMismatch'],
      });
      return;
    }

    if (hasAmbiguousRows && !allAmbiguousResolved) {
      // First click reveals the Employer column and the status banner so the user can
      // resolve; the button then stays disabled (with a tooltip) until they have.
      setIsValidated(true);
      return;
    }

    setIsValidated(true);

    // Employer picks live client-side until confirmed, so re-validate before importing
    // rather than making the user press a second button.
    let importable = isCorrectAllRecords;
    if (hasAmbiguousRows) {
      const fresh = await runReValidate();
      if (!fresh) return;
      importable = !!fresh.isCorrectAllRecords;
    }

    if (importable) {
      AppConfirmation.open({
        title: messages['common.success'],
        paragraph: formatMessage(
          { id: 'import.successMappedFields' },
          { fileName },
        ),
        onConfirm: async () => {
          if (!id) return;
          try {
            const res = await saveImportTimesheetById(id, {
              ...(selectedPayFrequency === PaymentFrequencyEnum.MONTHLY
                ? { monthConfig: selectedTaxPeriod }
                : { taxPeriodConfig: selectedTaxPeriod }),
              isBureau,
            });
            if (res.success) {
              showMessage({
                type: 'info',
                content: messages['import.timesheet.importPending'],
              });
              toggle();
              refetchData?.();
            }
          } catch (error) {
            console.error(error);
          }
        },
        type: 'success',
        icon: 'IconCheck',
      });
    } else {
      AppConfirmation.open({
        title: messages['common.validate.error'],
        paragraph: messages['import.agency.importError'],
        type: 'warning',
        icon: 'IconWarning',
        showFooterButton: false,
      });
    }
    setIsValidated(true);
  };

  const handleChangePayFrequency = (value: PayFrequencyValue | null) => {
    setSelectedPayFrequency(value ?? null);
    if (value === PaymentFrequencyEnum.MONTHLY) {
      setSelectedTaxPeriod(currentTaxMonth);
    } else if (value === PaymentFrequencyEnum.TWO_WEEKLY) {
      setSelectedTaxPeriod(
        currentTaxWeek
          ? currentTaxWeek % 2 === 0
            ? currentTaxWeek
            : currentTaxWeek + 1
          : null,
      );
    } else {
      setSelectedTaxPeriod(currentTaxWeek);
    }
  };

  // Only gate AFTER the first Validate click: the Employer column appears once
  // isValidated is set, so blocking earlier would deadlock the user (nothing to
  // resolve with, yet unable to reveal it).
  const validateBlockedReason =
    isValidated && unresolvedRowCount > 0
      ? `${unresolvedRowCount} row(s) still need an employer`
      : '';

  const isConfirmDisabled =
    loading ||
    saveImportTimesheetByIdLoading ||
    validateImportTimesheetLoading ||
    !!validateBlockedReason;

  // Ambiguous rows for rowSelection — keyed by their index in dataTable (offset -1 from data)
  const ambiguousDataTableIndices = useMemo(() => {
    return new Set(ambiguousRowIndices);
  }, [ambiguousRowIndices]);

  return (
    <AppModalContainer
      isOpen={isOpen}
      toggle={toggle}
      title={messages['bulkUpdate.modal.fileInformation']}
      width="90%"
      minHeight="0px"
    >
      <div className="flex justify-between mb-4 flex-wrap">
        <UploadedFileInformation
          fileName={fileName}
          fileSize={fileSize}
          totalRecords={totalRecords}
        />
        <div className="flex gap-4 flex-wrap">
          <AppSelect
            options={importTimesheetTemplateOptions}
            value={importTimesheetTemplateOptions[0]}
            allowClear={false}
            className="!min-w-0 !w-fit max-sm:!w-full"
          />
          <AppPayFrequencySelect
            placeholder={messages['common.payFrequency']}
            value={selectedPayFrequency}
            onChange={handleChangePayFrequency}
            allowClear={true}
            className="max-sm:!w-full"
          />
          <TaxWeekMonthSelect
            placeholder={messages['common.taxPeriod']}
            onFilter={(e) => setSelectedTaxPeriod(e.taxPeriod ?? null)}
            value={selectedTaxPeriod}
            paymentFrequency={selectedPayFrequency ?? 'WEEKLY'}
            disabled={!selectedPayFrequency}
            allowClear={true}
            className="max-sm:!w-full"
          />
          <div className="flex items-center">{`${messages['common.batchTotal']}: ${showCurrencyByCountry(payrollCountry)} ${formatThousands(
            sumRateLines(dataChecked.flatMap((row) => row?.rates || [])),
          )}`}</div>
          <Tooltip title={validateBlockedReason}>
            <span>
              <AppButton
                name={messages['common.validate']}
                type="primary"
                onClick={handleValidate}
                loading={
                  saveImportTimesheetByIdLoading || validateImportTimesheetLoading
                }
                disabled={isConfirmDisabled}
              />
            </span>
          </Tooltip>
        </div>
      </div>

      {/* Status only — no controls. Explains both ways to resolve. */}
      {isValidated && unresolvedRowCount > 0 && (
        <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
          <span className="font-medium">
            {`${unresolvedRowCount} of ${ambiguousRowIndices.length} row(s) still need an employer.`}
          </span>{' '}
          Pick one in the Employer column, or tick rows to apply in bulk.
        </div>
      )}

      {/* Bulk bar appears only once rows are ticked, so no control ever sits
          disabled without an obvious reason. */}
      {selectedAmbiguousRowKeys.length > 0 && (
        <div className="sticky top-0 z-10 flex items-center gap-3 mb-3 p-3 bg-white border border-gray-300 rounded shadow-sm">
          <span className="text-sm font-medium">
            {`${selectedAmbiguousRowKeys.length} row(s) selected`}
          </span>
          <Select
            size="large"
            style={{ minWidth: 220 }}
            placeholder={
              bulkEmployerOptions.length === 0
                ? 'No employer common to these rows'
                : 'Apply employer'
            }
            options={bulkEmployerOptions}
            value={bulkEmployerValue}
            onChange={setBulkEmployerValue}
            disabled={bulkEmployerOptions.length === 0}
            allowClear
          />
          <AppButton
            name="Apply"
            type="primary"
            onClick={handleBulkApply}
            disabled={!bulkEmployerValue}
          />
          <AppButton
            name="Clear selection"
            onClick={() => handleAmbiguousSelectionChange([])}
          />
        </div>
      )}

      <AppTableContainer
        data={dataTable}
        loading={loading}
        page={0}
        total={totalRecords}
        pageSize={totalRecords}
        columns={columns}
        // Rows are raw Excel value arrays with no `id`, so the container's default
        // rowKey='id' yields undefined keys and rowSelection can never match the
        // index-based keys used for ambiguous-row bulk apply.
        rowKey={(_: unknown, index?: number) => index as number}
        scroll={{ x: !isValidated ? 1000 : 1700, y: 500 }}
        // Virtual mode resolves row keys without an index, which breaks the index-keyed
        // ambiguous-row selection, so state `false` explicitly while resolving: omitting
        // the prop means "auto", and the container turns virtual on from 100 rows.
        virtual={hasAmbiguousRows ? false : totalRecords > 1000}
        {...(isValidated && hasAmbiguousRows
          ? {
              rowSelection: {
                type: 'checkbox' as const,
                selectedRowKeys: selectedAmbiguousRowKeys,
                onChange: (keys: React.Key[]) => {
                  // Only allow selecting ambiguous rows
                  const filtered = (keys as number[]).filter((k) =>
                    ambiguousDataTableIndices.has(k),
                  );
                  handleAmbiguousSelectionChange(filtered);
                },
                // antd passes only the record here, so the row index has to be recovered
                // from the data itself — taking it as a second argument silently yielded
                // `undefined`, which left every checkbox enabled.
                getCheckboxProps: (record: any) => ({
                  disabled: !ambiguousDataTableIndices.has(
                    dataTable.indexOf(record),
                  ),
                }),
              },
            }
          : {})}
      />

      <Modal
        open={employerPickerRowIndex !== null}
        title="Select employer"
        onCancel={() => setEmployerPickerRowIndex(null)}
        footer={null}
        width={760}
      >
        {employerPickerRowIndex !== null && (
          <>
            <p className="mb-3 text-sm text-gray-600">
              {mergedDataChecked[employerPickerRowIndex]?.ambiguousAgencyRef ? (
                <>
                  Reference{' '}
                  <span className="font-medium">
                    {
                      mergedDataChecked[employerPickerRowIndex]
                        ?.ambiguousAgencyRef
                    }
                  </span>{' '}
                  matches an employee under more than one employer.
                </>
              ) : (
                'This reference matches an employee under more than one employer.'
              )}{' '}
              Review the records below and pick the correct one for this row.
            </p>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b">
                  <th className="py-2 pr-3">Forename</th>
                  <th className="py-2 pr-3">Surname</th>
                  <th className="py-2 pr-3">NI Number</th>
                  <th className="py-2 pr-3">Employer</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {(
                  mergedDataChecked[employerPickerRowIndex]?.employerOptions ??
                  []
                ).map((opt: EmployerOption) => {
                  const isSelected =
                    opt.companyPaymentId ===
                    mergedDataChecked[employerPickerRowIndex]
                      ?.selectedCompanyPaymentId;
                  return (
                    <tr
                      key={opt.companyPaymentId}
                      className="border-b last:border-0"
                    >
                      <td className="py-2 pr-3">{opt.forename}</td>
                      <td className="py-2 pr-3">{opt.surname}</td>
                      <td className="py-2 pr-3">{opt.NINumber}</td>
                      <td className="py-2 pr-3">{opt.companyName}</td>
                      <td className="py-2 text-right">
                        <AppButton
                          name={isSelected ? 'Selected' : 'Select'}
                          type="primary"
                          disabled={isSelected}
                          onClick={() => {
                            handleRowEmployerSelect(
                              employerPickerRowIndex,
                              opt.companyPaymentId,
                            );
                            setEmployerPickerRowIndex(null);
                          }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
      </Modal>
    </AppModalContainer>
  );
};

export default ImportTimesheetResultUploadModal;
